/**
 * The card, editable.
 *
 * The seed data has now been wrong twice — a phantom Bronze tee, and two tees
 * carrying the course par instead of their own — and both times the only fix
 * was editing source and redeploying, from a golf resort, over resort wifi.
 * The admin standing in the pro shop with the physical card in his hand is the
 * one person who can settle every doubtful cell, and until this screen existed
 * he had no way to write down what he was looking at.
 *
 * Everything here edits a draft that Admin autosaves, so there is no save
 * button and no "are you sure": the protection is validation that runs live
 * and says exactly which cell it disbelieves, while the numbers are still in
 * front of the person holding the card.
 */

import { useState } from 'react'
import { cardProblems, teeProblems } from '../engine/handicap'
import type { Course, Hole, Player, TeeSet, TeeSetId } from '../engine/types'

/*
 * Blank and unparseable both land as 0 rather than being refused.
 *
 * A refused keystroke on a controlled input is invisible — the digit simply
 * does not appear and the admin retypes it harder. A 0 is visible AND flagged:
 * par 0 is outside 3..6, stroke index 0 breaks the permutation, and the
 * message names the hole. Half-typed is this screen's normal state, and the
 * live report is what makes that safe.
 */
const num = (raw: string): number => {
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

export function CourseEditor({
  course,
  players,
  rescoresPlayedGolf,
  onChange,
}: {
  course: Course
  /** The roster, so removing a tee can say whose card it is about to change. */
  players: Player[]
  /** True once `doc.handicaps` has rows — the moment edits stop reaching played rounds. */
  /**
   * Whether anything has been played or signed for on this course yet.
   *
   * Was `handicaps.length > 0` — the freeze — which is a different question and
   * false on every live document we have, including a dry run that was fully
   * played and signed. Pops are re-cut from this card on every render, so
   * moving a stroke index re-decides holes on cards men have already put their
   * names to, and the freeze cannot see it: it pins how many strokes a man
   * gets, never where they fall.
   */
  rescoresPlayedGolf: boolean
  onChange: (next: Course) => void
}) {
  const [armedTee, setArmedTee] = useState<TeeSetId | null>(null)
  const [newTee, setNewTee] = useState('')

  const holes = [...course.holes].sort((a, b) => a.number - b.number)

  const patchHole = (number: number, patch: Partial<Hole>) =>
    onChange({
      ...course,
      holes: course.holes.map((h) => (h.number === number ? { ...h, ...patch } : h)),
    })

  /*
   * Blank yardage DELETES the cell rather than storing a 0.
   *
   * Missing and zero both render as a dot and both pass validation, but they
   * say different things: absent is "the card did not give one", 0 is a number
   * somebody typed. The transcription rule has always been that an absent
   * yardage beats an invented one, and clearing a doubtful cell is how an
   * admin un-invents it.
   */
  const setYards = (number: number, teeId: TeeSetId, raw: string) =>
    onChange({
      ...course,
      holes: course.holes.map((h) => {
        if (h.number !== number) return h
        const yards = { ...h.yards }
        if (raw.trim() === '' || !Number.isFinite(Number(raw))) delete yards[teeId]
        else yards[teeId] = Number(raw)
        return { ...h, yards }
      }),
    })

  const patchTee = (id: TeeSetId, patch: Partial<TeeSet>) =>
    onChange({
      ...course,
      tees: course.tees.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    })

  /** Who is set to play this tee on this course — the people a removal reaches. */
  const assignedTo = (teeId: TeeSetId) =>
    players.filter((p) => p.teeByCourse[course.id] === teeId)

  /*
   * Armed like Remove on a round, and for the same reason: it sits a thumb's
   * width from live inputs. The first tap is where the damage report goes —
   * naming the players on the tee — because `findTee` will quietly walk them
   * to another tee's numbers the moment this one is gone, and "quietly" is the
   * word this whole screen exists to remove.
   *
   * The tee's yardage column stays in the holes. It is transcribed data that
   * cost somebody real effort, it renders nowhere once the tee is off the
   * list, and re-adding a tee under the same id gets it all back — which turns
   * a mistaken removal from a retyping job into a second tap.
   */
  const removeTee = (teeId: TeeSetId) => {
    /*
     * A course must keep one tee. `findTee` falls back to `tees[0]`, so with
     * none `buildContext` dereferences undefined and takes the week view, the
     * card, the grid, the board AND this screen down together — before the
     * autosave has fired, so the last edits go with it.
     */
    if (course.tees.length <= 1) return
    if (armedTee !== teeId) {
      setArmedTee(teeId)
      return
    }
    setArmedTee(null)
    onChange({ ...course, tees: course.tees.filter((t) => t.id !== teeId) })
  }

  /*
   * The id comes from the name, the way a player's does — and that is not
   * cosmetic. Every Bandon card uses the same tee ids, and `findTee` follows a
   * man's choice across courses BY ID: an admin adding the Royal Blue this
   * card is missing types "Royal Blue", gets `royalblue`, and everybody's
   * cross-course tee choice immediately understands the new column.
   */
  const addTee = () => {
    const name = newTee.trim()
    if (!name) return
    const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '') || 'tee'
    let id = base
    for (let i = 2; course.tees.some((t) => t.id === id); i += 1) id = `${base}${i}`
    onChange({
      ...course,
      tees: [
        ...course.tees,
        /*
         * Defaults chosen so a freshly added row raises no false alarms:
         * rating equal to par keeps the "this par is not this tee's" check
         * quiet, 113 is the definition of neutral slope, and 0 yards trips
         * nothing until holes are typed. The admin adding a tee is holding
         * the card with the real numbers on it — blank-and-quiet beats
         * plausible-and-wrong, which is how the seed data went bad.
         */
        {
          id,
          name,
          color: '#767676',
          rating: course.par,
          slope: 113,
          par: course.par,
          totalYards: 0,
        },
      ],
    })
    setNewTee('')
  }

  const problems = cardProblems(course)
  const errors = problems.filter((p) => p.kind !== 'convention')
  const warnings = problems.filter((p) => p.kind === 'convention')
  const teeIssues = teeProblems(course)

  /*
   * A plain function, not a component. Declared inside the render as `<Block>`
   * it would be a new component type every keystroke — React would unmount and
   * remount the whole table, and the input being typed into would drop focus
   * after every digit. Called as a function it is just inline JSX, and the
   * inputs keep their identity across renders.
   */
  const block = (label: string, hs: Hole[]) => (
    <>
      <tr className="bg-ink text-paper">
        <th className="bc-label text-left px-2 py-1 sticky left-0 bg-ink">{label}</th>
        {hs.map((h) => (
          <th key={h.number} className="bc-num px-1 py-1">
            {h.number}
          </th>
        ))}
        <th className="bc-label px-2 py-1">Tot</th>
      </tr>
      <tr className="bg-wash">
        <td className="bc-label text-left px-2 py-1 sticky left-0 bg-wash">Par</td>
        {hs.map((h) => (
          <td key={h.number} className="p-0.5">
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={h.par || ''}
              onChange={(e) => patchHole(h.number, { par: num(e.target.value) })}
              onFocus={(e) => e.currentTarget.select()}
              aria-label={`Hole ${h.number} par`}
              className="bc-tap w-10 h-10 text-center bc-num border border-ink bg-paper"
            />
          </td>
        ))}
        <td className="bc-num px-2 py-1 text-center">{hs.reduce((s, h) => s + h.par, 0)}</td>
      </tr>
      <tr className="border-b border-rule">
        <td className="bc-label text-left px-2 py-1 sticky left-0 bg-paper text-signal">
          Index
        </td>
        {hs.map((h) => (
          <td key={h.number} className="p-0.5">
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              value={h.strokeIndex || ''}
              onChange={(e) => patchHole(h.number, { strokeIndex: num(e.target.value) })}
              onFocus={(e) => e.currentTarget.select()}
              aria-label={`Hole ${h.number} stroke index`}
              className="bc-tap w-10 h-10 text-center bc-num border border-ink bg-paper text-signal"
            />
          </td>
        ))}
        <td />
      </tr>
      {course.tees.map((t) => (
        <tr key={t.id} className="border-b border-rule">
          <td className="bc-label text-left px-2 py-1 sticky left-0 bg-paper text-mute">
            {t.name}
          </td>
          {hs.map((h) => (
            <td key={h.number} className="p-0.5">
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={3}
                value={h.yards[t.id] ?? ''}
                onChange={(e) => setYards(h.number, t.id, e.target.value)}
                onFocus={(e) => e.currentTarget.select()}
                aria-label={`${t.name} yards on hole ${h.number}`}
                className="bc-tap w-11 h-10 text-center border border-rule bg-paper text-mute tabular-nums"
              />
            </td>
          ))}
          <td className="px-2 py-1 text-center text-mute tabular-nums">
            {hs.reduce((s, h) => s + (h.yards[t.id] ?? 0), 0) || '·'}
          </td>
        </tr>
      ))}
    </>
  )

  return (
    <div className="mt-2">
      {/*
        This warning used to say the opposite, and the audit caught it saying
        so: "a fix here reaches nothing already written." False. The freeze
        pins one NUMBER per man per round — tee, index, course handicap — and
        nothing else. The pops are re-cut from the live card's stroke indexes
        on every render, and quota reads the live pars, so editing either
        re-scores every round played on this course, signed or not: the
        verifier flipped a settled match from all-square to 1-up by swapping
        two stroke indexes with the handicaps frozen.

        Said truthfully now, and deliberately NOT blocked: the editor exists
        because a card can be discovered wrong mid-week, and a real misprint
        SHOULD re-score the rounds it lied to — that is the app's whole
        derive-everything design. What must never happen is the admin doing it
        unknowingly. If a signed result moves, the group decides what to do
        about the money; the app's job is that they know it moved.
      */}
      {rescoresPlayedGolf && (
        <p className="border-2 border-signal px-3 py-2 text-[13px] leading-snug mb-2">
          <b className="bc-name">Careful: this card re-scores golf already played.</b>{' '}
          Pops are cut from the stroke indexes on this card and quota reads its pars, on
          every render — so moving either re-decides holes on cards men have already
          signed, and writing the handicaps down does not stop it: that pins how many
          strokes a man gets, never where they fall. Fix a genuine misprint and tell the
          group what moved; do not tidy a live card.
        </p>
      )}

      {/*
        Course par beside the card it disciplines. The per-hole pars are
        checked against this number, so it has to be correctable in the same
        breath — Bandon Trails' card once said par 72 over holes that added to
        71, and there was no way to say which side was wrong.
      */}
      <label className="flex items-center gap-2 mb-2">
        <span className="bc-label text-mute">Course par</span>
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          value={course.par || ''}
          onChange={(e) => onChange({ ...course, par: num(e.target.value) })}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={`${course.name} course par`}
          className="bc-tap w-12 h-10 text-center bc-num border border-ink bg-paper"
        />
      </label>

      <div className="-mx-4">
        <div className="overflow-x-auto">
          <table className="text-[13px] border-collapse min-w-max">
            <tbody>
              {block('Out', holes.slice(0, 9))}
              {/* A nine-hole card has no back nine, and an empty "In" block of
                  dead inputs would read as eighteen cells waiting to be typed. */}
              {holes.length > 9 && (
                <>
                  <tr className="h-2" />
                  {block('In', holes.slice(9))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/*
        The report, live and inline. Never a blocked save — Setup autosaves and
        a half-typed card is its normal state — so the split is by severity
        instead: the signal box is a card the app will score wrongly, the quiet
        one is legal but suspicious. Stroke index errors always land in the
        first box, because a transposed index is invisible everywhere else.
      */}
      {errors.length > 0 && (
        <div className="mt-3 border-2 border-signal">
          <p className="bg-signal text-paper px-3 py-1.5 bc-label">
            {errors.length} problem{errors.length === 1 ? '' : 's'} on the card
          </p>
          <ul className="divide-y divide-rule">
            {errors.map((p) => (
              <li key={p.detail} className="px-3 py-2 text-[13px] leading-snug">
                {p.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mt-3 border border-rule">
          <p className="bg-wash px-3 py-1.5 bc-label text-mute">
            Worth a second look — legal, but off the pattern
          </p>
          <ul className="divide-y divide-rule">
            {warnings.map((p) => (
              <li key={p.detail} className="px-3 py-2 text-[13px] text-mute leading-snug">
                {p.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <h3 className="bc-label border-t-[3px] border-ink pt-2 mt-4">Tees</h3>
      <p className="text-[13px] text-mute leading-snug mt-1 mb-2">
        Rating, slope and PAR are this tee&rsquo;s own numbers off the physical
        card, not the course&rsquo;s — a tee carrying the course par instead of
        its own moves every course handicap on it, which is how two tees got
        deleted from this app.
      </p>
      <ul className="grid gap-3">
        {course.tees.map((t) => {
          const on = assignedTo(t.id)
          return (
            <li key={t.id} className="border border-rule p-2">
              <div className="flex items-center gap-2">
                <input
                  value={t.name}
                  onChange={(e) => patchTee(t.id, { name: e.target.value })}
                  aria-label={`Tee name for ${t.name}`}
                  className="bc-tap flex-1 min-w-0 border-b border-rule px-1 py-1.5 bc-name text-sm bg-paper"
                />
                <button
                  type="button"
                  onClick={() => removeTee(t.id)}
                  onBlur={() => armedTee === t.id && setArmedTee(null)}
                  className={`bc-label bc-tap px-2 shrink-0 ${
                    armedTee === t.id ? 'bg-signal text-paper' : 'text-signal'
                  }`}
                >
                  {armedTee === t.id ? 'Tap again' : 'Remove'}
                </button>
              </div>
              {/*
                The consequence, named, at the moment of the first tap. A tee
                with players on it does not refuse to go — mid-week the right
                call might well be removing it — but "Tyler and Chris are on
                it" is the difference between a decision and an accident.
              */}
              {armedTee === t.id && (
                <p className="text-[13px] text-signal leading-snug mt-1.5">
                  {on.length > 0 ? (
                    <>
                      <b className="bc-name">
                        {on.map((p) => p.displayName).join(' and ')}
                      </b>{' '}
                      {on.length === 1 ? 'is' : 'are'} on this tee — removing it
                      moves {on.length === 1 ? 'him' : 'them'} to another
                      tee&rsquo;s numbers. Tap again to remove it anyway.
                    </>
                  ) : (
                    'Nobody is on this tee. Tap again to remove it.'
                  )}
                </p>
              )}
              <div className="grid grid-cols-4 gap-1.5 mt-2">
                <label className="min-w-0">
                  <span className="bc-label text-mute text-[10px] block mb-0.5">Rating</span>
                  <input
                    type="number"
                    step="0.1"
                    inputMode="decimal"
                    value={t.rating}
                    onChange={(e) => patchTee(t.id, { rating: num(e.target.value) })}
                    aria-label={`${t.name} rating`}
                    className="bc-tap w-full min-w-0 text-center border border-ink px-0.5 py-2 bc-num bg-paper"
                  />
                </label>
                <label className="min-w-0">
                  <span className="bc-label text-mute text-[10px] block mb-0.5">Slope</span>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={3}
                    value={t.slope || ''}
                    onChange={(e) => patchTee(t.id, { slope: num(e.target.value) })}
                    aria-label={`${t.name} slope`}
                    className="bc-tap w-full min-w-0 text-center border border-ink px-0.5 py-2 bc-num bg-paper"
                  />
                </label>
                <label className="min-w-0">
                  <span className="bc-label text-mute text-[10px] block mb-0.5">Par</span>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={2}
                    value={t.par || ''}
                    onChange={(e) => patchTee(t.id, { par: num(e.target.value) })}
                    aria-label={`${t.name} par`}
                    className="bc-tap w-full min-w-0 text-center border border-ink px-0.5 py-2 bc-num bg-paper"
                  />
                </label>
                <label className="min-w-0">
                  <span className="bc-label text-mute text-[10px] block mb-0.5">Yards</span>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={t.totalYards || ''}
                    onChange={(e) => patchTee(t.id, { totalYards: num(e.target.value) })}
                    aria-label={`${t.name} total yards`}
                    className="bc-tap w-full min-w-0 text-center border border-ink px-0.5 py-2 bc-num bg-paper"
                  />
                </label>
              </div>
            </li>
          )
        })}
      </ul>

      {/* The tee-level report, beside the fields that fix it. */}
      {teeIssues.length > 0 && (
        <div className="mt-3 border-2 border-signal">
          <p className="bg-signal text-paper px-3 py-1.5 bc-label">
            {teeIssues.length} tee number{teeIssues.length === 1 ? '' : 's'} to check
          </p>
          <ul className="divide-y divide-rule">
            {teeIssues.map((x) => (
              <li key={`${x.teeId}-${x.kind}`} className="px-3 py-2">
                <span className="bc-name text-[13px]">{x.teeName}</span>
                <span className="text-[12px] text-mute"> — {x.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <input
          value={newTee}
          onChange={(e) => setNewTee(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addTee()
            }
          }}
          placeholder="Add a tee — name it as the card does"
          className="bc-tap flex-1 min-w-0 border-2 border-ink px-3 py-2 bc-name text-sm bg-paper"
        />
        <button
          type="button"
          onClick={addTee}
          disabled={!newTee.trim()}
          className="bc-tap bg-ink text-paper bc-label px-4 disabled:opacity-30"
        >
          Add
        </button>
      </div>

      {/*
        The same flag the course list ticks, restated here with its full
        meaning — because this screen is where somebody has just CHANGED the
        numbers, which is precisely the moment an old tick stops being true.
        Untick it after a doubtful edit; re-tick it once the card in your hand
        agrees with the card on screen.
      */}
      <label className="flex items-start gap-3 mt-4 border-t border-rule pt-3">
        <input
          type="checkbox"
          checked={course.verified}
          onChange={(e) => onChange({ ...course, verified: e.target.checked })}
          className="bc-tap mt-0.5 w-5 h-5 accent-[var(--color-ink)] shrink-0"
        />
        <span className="min-w-0">
          <span className="bc-name text-sm block">Checked against the physical card</span>
          <span className="text-[13px] text-mute leading-snug block">
            Ticked means a human held the physical card next to these numbers and
            they matched — not that they look plausible. If you have just changed
            something you were not reading off the card, untick it.
          </span>
        </span>
      </label>
    </div>
  )
}
